<template>
  <div class="search-page">
    <section class="hero">
      <div class="hero-content">
        <div class="hero-text">
          <h1>🔥 AI 驱动的视频爆款筛选</h1>
          <p>输入关键词，联动多平台爬虫抓取视频元数据，应用AI模型计算爆款评分（0-100），快速发现高传播潜力的爆款内容。</p>
          <div class="hero-actions">
            <button class="primary" @click="focusKeyword">开始搜索</button>
            <router-link class="secondary" to="/tasks">解析视频链接</router-link>
          </div>
        </div>
        <div class="hero-cards">
          <div class="hero-card">
            <span class="icon">🔍</span>
            <h3>跨平台检索</h3>
            <p>聚合 B站 / YouTube / 抖音 / 小红书，一键搜索全网热门内容。</p>
          </div>
          <div class="hero-card">
            <span class="icon">🤖</span>
            <h3>AI 爆款评分</h3>
            <p>基于播放量、互动率、时长等多维度计算 0-100 爆款分数。</p>
          </div>
          <div class="hero-card">
            <span class="icon">📊</span>
            <h3>智能筛选</h3>
            <p>按播放量、点赞数、时长等条件精准筛选爆款候选。</p>
          </div>
        </div>
      </div>
    </section>

    <section class="search-panel">
      <div class="panel-header">
        <div>
          <h2>视频爆款筛选</h2>
          <p>支持关键词搜索、多条件筛选、AI 爆款评分排序</p>
        </div>
        <div class="status-pills">
          <span class="pill"><strong>{{ stats.total }}</strong> 总结果</span>
          <span v-if="stats.hasMore" class="pill more">更多结果可继续筛选</span>
        </div>
      </div>

      <div class="filters">
        <div class="filter-main">
          <div class="input-group keyword-input">
            <label for="keyword">搜索关键词</label>
            <HistoryInput
              v-model="searchForm.keyword"
              placeholder="如：短视频爆款、旅游 vlog、好物推荐"
              size="large"
              storage-key="search_keyword"
              :max-history="15"
              @enter="handleSearch"
              ref="keywordInputRef"
            />
          </div>

          <div class="select-group">
            <div class="input-group">
              <label for="platform">目标平台</label>
              <select id="platform" v-model="searchForm.platform">
                <option v-for="option in platformOptions" :key="option.value" :value="option.value">
                  {{ option.label }}
                </option>
              </select>
            </div>
            <div class="input-group">
              <label for="limit">返回数量</label>
              <select id="limit" v-model.number="searchForm.limit">
                <option :value="10">10 条</option>
                <option :value="20">20 条</option>
                <option :value="50">50 条</option>
                <option :value="100">100 条</option>
              </select>
            </div>
            <div class="input-group">
              <label for="sortBy">排序方式</label>
              <select id="sortBy" v-model="searchForm.sortBy">
                <option value="viral_score">🔥 爆款评分</option>
                <option value="views">👁 播放量</option>
                <option value="likes">❤️ 点赞数</option>
              </select>
            </div>
          </div>
        </div>

        <div class="filter-advanced">
          <div class="input-group">
            <label>最小播放量</label>
            <input v-model="searchForm.minViews" type="number" min="0" placeholder="如：10000" />
          </div>
          <div class="input-group">
            <label>最小点赞数</label>
            <input v-model="searchForm.minLikes" type="number" min="0" placeholder="如：1000" />
          </div>
          <div class="input-group">
            <label>最大时长 (秒)</label>
            <input v-model="searchForm.maxDuration" type="number" min="0" placeholder="如：180" />
          </div>
          <button class="search-button" :disabled="loading" @click="handleSearch">
            <span v-if="loading">🔄 检索中...</span>
            <span v-else>🚀 立即检索</span>
          </button>
        </div>
      </div>

      <div v-if="error" class="alert error">{{ error }}</div>
      
      <div v-if="Object.keys(stats.errors).length" class="alert warning">
        <p>⚠️ 部分平台暂不可用：</p>
        <ul>
          <li v-for="(message, platform) in stats.errors" :key="platform">
            <strong>{{ getPlatformName(platform) }}</strong>：{{ message }}
          </li>
        </ul>
      </div>

      <div v-if="Object.keys(stats.sourceCounts).length" class="source-summary">
        <div v-for="(count, platform) in stats.sourceCounts" :key="platform" class="summary-card">
          <span class="platform-icon">{{ getPlatformIcon(platform) }}</span>
          <span class="label">{{ getPlatformName(platform) }}</span>
          <span class="value">{{ count }}</span>
        </div>
      </div>
    </section>

    <section v-if="results.length" class="results">
      <div class="results-header">
        <h2>🔥 爆款候选视频</h2>
        <span class="results-count">共 {{ results.length }} 条，按{{ getSortLabel() }}排序</span>
      </div>
      <div class="results-grid">
        <article class="video-card" :class="{ 'demo-card': isDemoVideo(video) }" v-for="video in results" :key="`${video.platform}-${video.id}`">
          <div class="demo-badge" v-if="isDemoVideo(video)">🔥 热搜推荐</div>
          <div class="card-top">
            <div class="viral-badge" :class="getGradeClass(video.viral_grade)">
              <span class="score">{{ video.viral_score }}</span>
              <span class="grade">{{ video.viral_grade }}</span>
            </div>
            <div class="card-meta">
              <span class="platform-tag" :class="video.platform">{{ getPlatformName(video.platform) }}</span>
              <span class="duration">⏱ {{ formatDuration(video.duration) }}</span>
            </div>
          </div>
          
          <h3 class="video-title">{{ cleanTitle(video.title) || '未命名视频' }}</h3>
          <p class="video-author">👤 {{ video.author || '未知创作者' }}</p>
          
          <div class="video-stats">
            <span class="stat"><span class="stat-icon">👁</span>{{ formatNumber(video.view_count) }}</span>
            <span class="stat"><span class="stat-icon">❤️</span>{{ formatNumber(video.like_count) }}</span>
            <span class="stat"><span class="stat-icon">💬</span>{{ formatNumber(video.comment_count) }}</span>
          </div>

          <div class="viral-factors" v-if="video.viral_factors && video.viral_factors.length">
            <span class="factor" v-for="(factor, idx) in video.viral_factors.slice(0, 2)" :key="idx">
              {{ factor }}
            </span>
          </div>
          
          <p class="viral-verdict" v-if="video.viral_verdict">{{ video.viral_verdict }}</p>
          
          <div class="card-actions">
            <a :href="resolveVideoUrl(video)" target="_blank" rel="noopener" class="link">
              {{ isDemoVideo(video) ? '搜索更多' : '查看原视频' }}
            </a>
            <router-link v-if="!isDemoVideo(video)" :to="{ path: '/tasks', query: { url: resolveVideoUrl(video) } }" class="action">
              🎯 深度分析
            </router-link>
            <span v-else class="action disabled" title="热搜推荐需要点击搜索更多查看具体视频">
              🔥 热搜话题
            </span>
          </div>
        </article>
      </div>
    </section>

    <section v-else-if="!loading && !error" class="empty">
      <div class="empty-card" style="background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%); border-radius: 16px; padding: 36px 24px; box-shadow: 0 4px 20px rgba(0,0,0,0.06); text-align: center; border: 1px solid #e2e8f0;">
        <div class="icon" style="font-size: 48px; margin-bottom: 16px;">🔥</div>
        <h3 style="font-size: 18px; color: #1e293b; margin-bottom: 8px;">输入关键词或点击下方热门方向，探索全网爆款</h3>
        <p style="font-size: 14px; color: #64748b; margin-bottom: 24px;">聚合 20+ 平台爬虫数据，基于播放/互动比智能评分（0-100分）</p>
        
        <div style="max-width: 600px; margin: 0 auto; display: flex; flex-wrap: wrap; justify-content: center; gap: 10px;">
          <el-tag effect="light" type="danger" style="cursor: pointer; padding: 8px 16px; font-size: 13px; font-weight: 500;" @click="quickSearch('AI编导短剧')">🔥 AI 编导短剧</el-tag>
          <el-tag effect="light" type="success" style="cursor: pointer; padding: 8px 16px; font-size: 13px; font-weight: 500;" @click="quickSearch('美食探店')">🍔 美食探店</el-tag>
          <el-tag effect="light" type="warning" style="cursor: pointer; padding: 8px 16px; font-size: 13px; font-weight: 500;" @click="quickSearch('数码评测')">💻 数码黑科技</el-tag>
          <el-tag effect="light" type="info" style="cursor: pointer; padding: 8px 16px; font-size: 13px; font-weight: 500;" @click="quickSearch('知识干货')">📚 知识干货</el-tag>
          <el-tag effect="light" type="primary" style="cursor: pointer; padding: 8px 16px; font-size: 13px; font-weight: 500;" @click="quickSearch('剪辑教程')">🎬 剪辑变现教程</el-tag>
          <el-tag effect="light" type="danger" style="cursor: pointer; padding: 8px 16px; font-size: 13px; font-weight: 500;" @click="quickSearch('美妆护肤')">💄 美妆好物</el-tag>
          <el-tag effect="light" type="success" style="cursor: pointer; padding: 8px 16px; font-size: 13px; font-weight: 500;" @click="quickSearch('搞笑剧情')">😂 搞笑剧场</el-tag>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, onMounted } from 'vue'
import { resolveApiUrl } from '@/utils/api'
import HistoryInput from '@/components/HistoryInput.vue'

interface VideoResult {
  id: string
  platform: string
  title: string
  author: string
  description?: string
  duration?: number
  video_url: string
  source_url?: string
  thumbnail_url?: string
  view_count?: number
  like_count?: number
  comment_count?: number
  viral_score?: number
  viral_grade?: string
  viral_factors?: string[]
  viral_verdict?: string
  tags?: string[]
}

interface SearchResponse {
  videos: VideoResult[]
  total: number
  has_more: boolean
  source_counts: Record<string, number>
  errors: Record<string, string>
}

const keywordInputRef = ref<InstanceType<typeof HistoryInput>>()
const loading = ref(false)
const error = ref('')
const results = ref<VideoResult[]>([])

const stats = reactive({
  total: 0,
  hasMore: false,
  sourceCounts: {} as Record<string, number>,
  errors: {} as Record<string, string>
})

const platformOptions = [
  { value: 'all', label: '🌐 全部平台' },
  { value: 'bili', label: '📺 B站' },
  { value: 'youtube', label: '▶️ YouTube' },
  { value: 'douyin', label: '🎵 抖音' },
  { value: 'xhs', label: '📕 小红书' }
]

const searchForm = reactive({
  keyword: '',
  platform: 'all',
  limit: 20,
  sortBy: 'viral_score',
  minViews: '',
  minLikes: '',
  maxDuration: ''
})

const buildParams = () => {
  const params = new URLSearchParams({
    keyword: searchForm.keyword,
    limit: String(searchForm.limit),
    platform: searchForm.platform,
    sort_by: searchForm.sortBy
  })

  if (searchForm.minViews) {
    params.append('min_views', searchForm.minViews)
  }

  if (searchForm.minLikes) {
    params.append('min_likes', searchForm.minLikes)
  }

  if (searchForm.maxDuration) {
    params.append('max_duration', searchForm.maxDuration)
  }

  return params
}

const handleSearch = async () => {
  if (!searchForm.keyword.trim()) {
    error.value = '请输入搜索关键词'
    results.value = []
    return
  }

  loading.value = true
  error.value = ''
  results.value = []

  try {
    const params = buildParams()
    const url = `${resolveApiUrl('/api/search/videos')}?${params.toString()}`
    const response = await fetch(url)

    if (!response.ok) {
      const body = await response.json().catch(() => ({}))
      throw new Error(body.detail || response.statusText)
    }

    const data = (await response.json()) as SearchResponse
    results.value = data.videos || []
    stats.total = data.total || 0
    stats.hasMore = data.has_more || false
    stats.sourceCounts = data.source_counts || {}
    stats.errors = data.errors || {}

    if (!results.value.length) {
      error.value = '未找到匹配的视频，请调整关键词或筛选条件'
    }
  } catch (err) {
    error.value = err instanceof Error ? `搜索失败：${err.message}` : '搜索时发生未知错误'
    console.error('Search error:', err)
  } finally {
    loading.value = false
  }
}

const quickSearch = (keyword: string) => {
  searchForm.keyword = keyword
  handleSearch()
}

const focusKeyword = () => {
  // HistoryInput doesn't expose focus, we can scroll to the input
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const getPlatformName = (value: string) => {
  const map: Record<string, string> = {
    all: '全部平台',
    douyin: '抖音',
    xhs: '小红书',
    bili: 'B站',
    youtube: 'YouTube',
    tiktok: 'TikTok',
    ks: '快手'
  }
  return map[value] || value
}

const getPlatformIcon = (value: string) => {
  const map: Record<string, string> = {
    douyin: '🎵',
    xhs: '📕',
    bili: '📺',
    youtube: '▶️',
    tiktok: '🎬'
  }
  return map[value] || '📹'
}

const getSortLabel = () => {
  const map: Record<string, string> = {
    viral_score: '爆款评分',
    views: '播放量',
    likes: '点赞数'
  }
  return map[searchForm.sortBy] || '爆款评分'
}

const getGradeClass = (grade?: string) => {
  if (!grade) return 'grade-c'
  return `grade-${grade.toLowerCase()}`
}

const resolveVideoUrl = (video: VideoResult) => video.source_url || video.video_url

const formatNumber = (value?: number) => {
  if (!value) return '0'
  if (value >= 100000000) return (value / 100000000).toFixed(1) + '亿'
  if (value >= 10000) return (value / 10000).toFixed(1) + '万'
  return value.toLocaleString('zh-CN')
}

const formatDuration = (seconds?: number) => {
  if (!seconds) return '未知'
  const minutes = Math.floor(seconds / 60)
  const secs = seconds % 60
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60)
    const mins = minutes % 60
    return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

// 判断是否是无法深度分析的数据（如热搜推荐、搜索页链接等）
const isDemoVideo = (video: VideoResult): boolean => {
  // 检查是否是热搜推荐数据（URL指向搜索页而非具体视频）
  if (video.source_url?.includes('/search/')) return true
  // 检查是否是热搜ID
  if (video.id?.startsWith('hot_')) return true
  // 检查作者是否是系统推荐
  if (video.author === '热搜推荐') return true
  return false
}

// 清理标题
const cleanTitle = (title?: string): string => {
  if (!title) return ''
  return title
}
</script>

<style scoped>
.search-page {
  display: flex;
  flex-direction: column;
  gap: 3rem;
  padding-bottom: 4rem;
}

.hero {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%);
  border-radius: 0 0 48px 48px;
  padding: 4rem 3rem 5rem;
  color: #ffffff;
  position: relative;
  overflow: hidden;
}

.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: 
    radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 50%);
  pointer-events: none;
}

.hero-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 3rem;
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
}

.hero-text {
  flex: 1 1 55%;
}

.hero-text h1 {
  font-size: 2.8rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  text-shadow: 0 2px 20px rgba(0,0,0,0.15);
}

.hero-text p {
  font-size: 1.1rem;
  line-height: 1.8;
  max-width: 560px;
  margin-bottom: 2rem;
  color: rgba(255, 255, 255, 0.95);
}

.hero-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.hero-actions .primary {
  background: #ffffff;
  color: #667eea;
  border: none;
  padding: 1rem 2rem;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.hero-actions .primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.2);
}

.hero-actions .secondary {
  color: #ffffff;
  border: 2px solid rgba(255, 255, 255, 0.6);
  padding: 1rem 2rem;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
}

.hero-actions .secondary:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: #ffffff;
}

.hero-cards {
  flex: 1 1 45%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1.2rem;
}

.hero-card {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 20px;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  transition: transform 0.3s ease;
}

.hero-card:hover {
  transform: translateY(-5px);
}

.hero-card .icon {
  font-size: 2rem;
  display: block;
  margin-bottom: 0.8rem;
}

.hero-card h3 {
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.hero-card p {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.5;
  margin: 0;
}

.search-panel {
  max-width: 1100px;
  margin: -4rem auto 0;
  padding: 2.5rem;
  background: #ffffff;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(102, 126, 234, 0.15);
  position: relative;
  z-index: 2;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.panel-header h2 {
  font-size: 1.75rem;
  color: #1f2937;
  margin-bottom: 0.4rem;
}

.panel-header p {
  color: #6b7280;
  margin: 0;
}

.status-pills {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.pill {
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #4338ca;
  padding: 0.6rem 1.2rem;
  border-radius: 999px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.pill strong {
  font-size: 1.2rem;
  font-weight: 700;
}

.pill.more {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #92400e;
}

.filters {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.filter-main {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.keyword-input {
  width: 100%;
}

.keyword-input input {
  width: 100%;
  font-size: 1.1rem;
  padding: 1rem 1.2rem;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.input-group label {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}

.input-group input,
.input-group select {
  padding: 0.9rem 1rem;
  border-radius: 12px;
  border: 2px solid #e5e7eb;
  background: #f9fafb;
  font-size: 0.95rem;
  color: #111827;
  transition: all 0.2s ease;
}

.input-group input:focus,
.input-group select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.15);
  background: #ffffff;
}

.select-group {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.filter-advanced {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  align-items: end;
}

.search-button {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #ffffff;
  border: none;
  padding: 1rem 1.5rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.search-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
}

.search-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.alert {
  border-radius: 12px;
  padding: 1.2rem 1.5rem;
  margin-top: 1.5rem;
  font-size: 0.95rem;
}

.alert.error {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  color: #b91c1c;
  border: 1px solid #fca5a5;
}

.alert.warning {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #92400e;
  border: 1px solid #fcd34d;
}

.alert.warning ul {
  margin: 0.5rem 0 0;
  padding-left: 1.5rem;
}

.source-summary {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
}

.summary-card {
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  border-radius: 16px;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  border: 1px solid #bae6fd;
}

.summary-card .platform-icon {
  font-size: 1.5rem;
}

.summary-card .label {
  font-size: 0.9rem;
  color: #0369a1;
  font-weight: 500;
}

.summary-card .value {
  font-size: 1.4rem;
  font-weight: 800;
  color: #0c4a6e;
  margin-left: auto;
}

.results {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.results-header h2 {
  font-size: 1.5rem;
  color: #1f2937;
}

.results-count {
  color: #6b7280;
  font-size: 0.95rem;
}

.results-grid {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
}

.video-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.video-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 50px rgba(102, 126, 234, 0.2);
  border-color: rgba(102, 126, 234, 0.3);
}

/* 演示数据卡片样式 */
.video-card.demo-card {
  border-color: rgba(234, 179, 8, 0.4);
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}

.video-card.demo-card:hover {
  border-color: rgba(234, 179, 8, 0.6);
  box-shadow: 0 20px 50px rgba(234, 179, 8, 0.2);
}

.demo-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: #78350f;
  padding: 0.3rem 0.8rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(245, 158, 11, 0.4);
}

.card-actions .action.disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
  pointer-events: none;
  opacity: 0.8;
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.viral-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.8rem 1rem;
  border-radius: 16px;
  min-width: 70px;
}

.viral-badge .score {
  font-size: 1.8rem;
  font-weight: 800;
  line-height: 1;
}

.viral-badge .grade {
  font-size: 0.75rem;
  font-weight: 700;
  margin-top: 0.2rem;
  opacity: 0.8;
}

.viral-badge.grade-s {
  background: linear-gradient(135deg, #fef3c7, #fde047);
  color: #854d0e;
}

.viral-badge.grade-a {
  background: linear-gradient(135deg, #dcfce7, #86efac);
  color: #166534;
}

.viral-badge.grade-b {
  background: linear-gradient(135deg, #e0f2fe, #7dd3fc);
  color: #0369a1;
}

.viral-badge.grade-c {
  background: linear-gradient(135deg, #f3f4f6, #d1d5db);
  color: #374151;
}

.viral-badge.grade-d,
.viral-badge.grade-e {
  background: linear-gradient(135deg, #fee2e2, #fca5a5);
  color: #991b1b;
}

.card-meta {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.4rem;
}

.platform-tag {
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
}

.platform-tag.douyin {
  background: rgba(0, 0, 0, 0.08);
  color: #000;
}

.platform-tag.xhs {
  background: rgba(255, 47, 47, 0.12);
  color: #ff2f2f;
}

.platform-tag.bili {
  background: rgba(0, 174, 236, 0.12);
  color: #00a1d6;
}

.platform-tag.youtube {
  background: rgba(255, 0, 0, 0.1);
  color: #ff0000;
}

.duration {
  font-size: 0.8rem;
  color: #6b7280;
}

.video-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.video-author {
  margin: 0;
  font-size: 0.85rem;
  color: #6b7280;
}

.video-stats {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.stat {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.85rem;
  color: #4b5563;
}

.stat-icon {
  font-size: 0.9rem;
}

.viral-factors {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.factor {
  font-size: 0.75rem;
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  color: #92400e;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
}

.viral-verdict {
  font-size: 0.85rem;
  color: #059669;
  margin: 0;
  font-weight: 500;
  line-height: 1.4;
}

.card-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 0.8rem;
  border-top: 1px solid #f3f4f6;
}

.link {
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  color: #667eea;
}

.link:hover {
  text-decoration: underline;
}

.action {
  color: #ffffff;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 0.6rem 1.2rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
}

.action:hover {
  transform: scale(1.05);
  box-shadow: 0 5px 20px rgba(102, 126, 234, 0.4);
}

.empty {
  max-width: 700px;
  margin: 0 auto;
  padding: 0 1.5rem;
}

.empty-card {
  background: #ffffff;
  border-radius: 24px;
  padding: 4rem 3rem;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
}

.empty-card .icon {
  font-size: 4rem;
  margin-bottom: 1.5rem;
}

.empty-card h3 {
  font-size: 1.5rem;
  color: #1f2937;
  margin-bottom: 1rem;
}

.empty-card p {
  color: #6b7280;
  line-height: 1.8;
  margin-bottom: 2rem;
}

.quick-tags {
  display: flex;
  justify-content: center;
  gap: 0.8rem;
  flex-wrap: wrap;
}

.quick-tags span {
  background: linear-gradient(135deg, #eef2ff, #e0e7ff);
  color: #4338ca;
  padding: 0.6rem 1.2rem;
  border-radius: 999px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.quick-tags span:hover {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: #ffffff;
  transform: scale(1.05);
}

@media (max-width: 768px) {
  .hero-content {
    flex-direction: column;
  }
  
  .hero-text h1 {
    font-size: 2rem;
  }
  
  .select-group {
    grid-template-columns: 1fr;
  }
  
  .filter-advanced {
    grid-template-columns: 1fr 1fr;
  }
  
  .results-grid {
    grid-template-columns: 1fr;
  }
}
</style>
